import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconS24CrossCircle24Filled } from '@is24/cosma-ui-icons'

import CSS from 'csstype'
import { ContentStateProperty, InputType } from '../../../../@types/types'
import { EntityObject } from '../../../../@types/geoAutocompletion-types'
import LocationLink from '../../buttons/locationLink/LocationLink'
import { cutToChosenDigitLength } from '../../../../services/conversion/conversionServices'
import { InputProps } from '../../../../@types/props-types'
import { useFormState } from '../../../pages/insertionPage/wizard/useFormState'
import { useStore } from 'react-redux'
import { InitState } from '../../../../redux/reducers/root/initialState'
import { useDoGeoAutocompletion } from './locationInput.hooks'
import { InputLabel } from '../../display/labelText/LabelText'

const KEY_ARROW_DOWN = 'ArrowDown'
const KEY_ARROW_UP = 'ArrowUp'
const KEY_ENTER = 'Enter'
const KEY_TAB = 'Tab'
const KEY_ARROW_LEFT = 'ArrowLeft'
const KEY_ARROW_RIGHT = 'ArrowRight'

export interface LocationInputProps extends InputProps {
  type?: InputType
  style: React.CSSProperties
  label?: string
  labelTooltip?: React.ReactChild
  input?: string
  maxLength?: number
  locations: EntityObject[]
  locationType: string | undefined
  errors: string[]
  onChange: (propertyName: ContentStateProperty, value: string | undefined, object?: EntityObject) => void
  onAddressChange?: (value: string, object: EntityObject) => void
  onFocus?: () => void
  onBlur?: () => void
  onBlurTracking?: (value: string | undefined, isUnchanged: boolean) => void
  dataHeyimmoContext?: string
}

const dropDownRoot: CSS.Properties = {
  position: 'relative',
  height: 0,
  zIndex: 2,
}

const dropDownList: CSS.Properties = {
  width: '100%',
  maxHeight: '500px',
  minWidth: '150px',
  overflowY: 'auto',
  WebkitBoxShadow: '0 4px 8px rgba(0, 0, 0, .175)',
  boxShadow: '0 4px 8px rgba(0, 0, 0, .175)',
}

const useIsMounted = () => {
  const isMounted = React.useRef(false)
  React.useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])
  return isMounted
}

export const LocationInput: React.FunctionComponent<LocationInputProps> = ({
  id,
  type,
  propertyName,
  style,
  label,
  labelTooltip,
  input,
  maxLength,
  locations,
  locationType,
  errors,
  onChange,
  onFocus,
  onBlur,
  onBlurTracking,
  dataHeyimmoContext,
}: LocationInputProps) => {
  const isMounted = useIsMounted()

  const { t } = useTranslation()

  const [state, setState] = useState<{
    hoveredLocation: string
    hasFocus: boolean
    hasDropDown: boolean
    isUnchanged: boolean
    lastValue: string | undefined
    currentInput: string | undefined
  }>({
    hoveredLocation: '0',
    hasFocus: false,
    hasDropDown: false,
    isUnchanged: true,
    lastValue: input,
    currentInput: input,
  })

  useEffect(() => setState((prev) => ({ ...prev, currentInput: input })), [input])
  const { setTouched, formState } = useFormState()
  const store = useStore<InitState>()
  const textInput = useRef<HTMLInputElement>(null)
  const hasErrors = () => errors.length > 0 && (formState.submitCount > 0 || formState.touched[propertyName])
  const getSuccessClass = () => (input !== undefined && !hasErrors() ? ' success-border' : '')
  const className = 'input-text' + (hasErrors() ? ' error' : '') + getSuccessClass()
  const showLocations =
    state.hasFocus && state.hasDropDown && !!locations.length && propertyName.includes(locationType as string)

  const onMouseOver = (locationId: string) => setState((prev) => ({ ...prev, hoveredLocation: locationId }))
  const onFocusCallback = () => {
    onFocus?.()
    setState((prev) => ({ ...prev, hoveredLocation: '0', hasFocus: true }))
  }

  const onBlurInput = ({ relatedTarget }: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(
      () =>
        isMounted.current &&
        setState((prev) => ({
          ...prev,
          hoveredLocation: '0',
          hasFocus: false,
          hasDropDown: false,
        })),
      150
    )

    if (relatedTarget?.id !== `location-input-container ${id}-container`) {
      onBlur && onBlur()
      setTouched(propertyName)
    }
    if (
      state.lastValue !== state.currentInput &&
      !(state.lastValue === undefined && state.currentInput === '') &&
      relatedTarget?.id !== `location-input-container ${id}-container`
    ) {
      onBlurTracking && onBlurTracking(state.currentInput, state.isUnchanged)
      setState((prev) => ({ ...prev, lastValue: state.currentInput, isUnchanged: false }))
    }
  }

  const onSelect = (locationId: string, locationLabel: string) => {
    const index: number = locations.findIndex((x) => x.id === locationId)
    onChange(propertyName, locationLabel, locations[index])
    textInput.current && textInput.current.focus()

    setTimeout(
      () =>
        isMounted.current &&
        setState((prev) => ({ ...prev, hasDropDown: false, hoveredLocation: '0', hasFocus: true })),
      150
    )
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.shiftKey) return
    const selected: EntityObject = locations.find((x) => x.id === state.hoveredLocation) ?? locations[0]
    const index: number = locations.findIndex((x) => x.id === selected.id)

    switch (event.key) {
      case KEY_ARROW_DOWN:
        event.preventDefault()
        locations.length &&
          setState((prev) => ({
            ...prev,
            hoveredLocation: locations[index < locations.length - 1 ? index + 1 : index].id,
          }))
        break
      case KEY_ARROW_UP:
        event.preventDefault()
        locations.length &&
          setState((prev) => ({
            ...prev,
            hoveredLocation: locations[index > 1 ? index - 1 : 0].id,
          }))
        break
      case KEY_TAB:
        if (locations.length && state.hasDropDown && index > 0) {
          onChange(propertyName, selected.label, locations[index])

          setState((prev) => ({ ...prev, hoveredLocation: '0', hasDropDown: false }))
          event.preventDefault()
        }
        break
      case KEY_ENTER:
        if (locations.length && state.hasDropDown) {
          onChange(propertyName, selected.label, locations[index])
          setState((prev) => ({ ...prev, hoveredLocation: '0', hasDropDown: false }))
          event.preventDefault()
        }
        break
      case KEY_ARROW_LEFT:
      case KEY_ARROW_RIGHT:
        break
      default:
        setState((prev) => ({ ...prev, hoveredLocation: '0' }))
    }
  }

  const doGeoAutocompletion = useDoGeoAutocompletion()

  const onChangeInput = (newValue: string) => {
    const value = maxLength !== undefined ? cutToChosenDigitLength(newValue, maxLength) : newValue
    setState((prev) => ({ ...prev, hasDropDown: true, currentInput: value }))
    onChange(propertyName, value)
    doGeoAutocompletion(propertyName, value, store.getState().insertionFeature.insertionData)
  }

  const formattedLabel = labelTooltip ? (
    labelTooltip
  ) : (
    <InputLabel id={id} labelTranslationKey={label ?? ''} propertyName={propertyName} />
  )

  return (
    <div id={`location-input-container ${id}-container`} className='input-text-container pii-hide' tabIndex={-1}>
      {formattedLabel}
      <input
        tabIndex={0}
        ref={textInput}
        type={type ? type : 'text'}
        id={id}
        className={className}
        value={state.currentInput ? state.currentInput : ''}
        style={style}
        {...(maxLength && { maxLength: maxLength })}
        autoComplete='nope'
        onChange={({ target: { value } }) => onChangeInput(value)}
        onFocus={onFocusCallback}
        onBlur={onBlurInput}
        onKeyDown={onKeyDown}
      />
      {dataHeyimmoContext && (
        <span data-heyimmo-context={dataHeyimmoContext} style={{ display: 'none' }} aria-hidden='true'>
          {state.currentInput ?? ''}
        </span>
      )}
      {showLocations && (
        <div className='DropDown' style={dropDownRoot}>
          <ul className='background-white border absolute-content' style={dropDownList}>
            {locations &&
              locations.map((location, index) => (
                <LocationLink
                  label={location.label}
                  id={location.id}
                  key={index}
                  textToHighlight={input}
                  hovered={location.id === state.hoveredLocation}
                  onMouseOver={onMouseOver}
                  onClick={onSelect}
                />
              ))}
          </ul>
        </div>
      )}
      {hasErrors() && (
        <div role='alert' className='font-error' aria-labelledby={id + '-input-label'}>
          <div className='caption caption--error'>
            <IconS24CrossCircle24Filled className={'caption-icon'} />
            {t(`validation.${errors[0]}`)}
          </div>
        </div>
      )}
    </div>
  )
}
